// ---------------------------------------------------------------------------
// PresetMenu — popover MUI para os "atalhos de IA" da regen V3.
//
// Aciona via `anchorEl` (passado pelo pai). Renderiza presets agrupados
// por categoria, com label + descricao curta. Click chama
// ``onSelectPreset(preset)`` (o pai e quem deposita o texto no input).
//
// Estilo: alinhado com o tom da view (cinzas claros, hover sutil). Usa
// `Popover` MUI ao inves de Menu para nao herdar o estilo de
// MenuItem (que e' alto demais e nao acomoda a linha de descricao).
// ---------------------------------------------------------------------------

import React from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { makeStyles } from "@material-ui/core/styles";
import Popover from "@material-ui/core/Popover";

import { REGEN_PRESETS, groupPresetsByCategory } from "./regen_presets";

const useStyles = makeStyles((theme) => ({
  paper: {
    width: 360,
    maxWidth: "92vw",
    maxHeight: "60vh",
    overflow: "auto",
    padding: "6px 0",
    border: "1px solid #e9ecef",
    boxShadow: "0 8px 24px rgba(0,0,0,0.16)",
  },
  group: {
    padding: "6px 0",
    "&:not(:last-child)": {
      borderBottom: "1px solid #f1f3f4",
    },
  },
  groupLabel: {
    padding: "4px 14px 2px 14px",
    color: "#9e9e9e",
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
  },
  item: {
    display: "flex",
    alignItems: "flex-start",
    gap: 10,
    padding: "7px 14px",
    cursor: "pointer",
    border: "none",
    background: "transparent",
    width: "100%",
    textAlign: "left",
    fontFamily: "inherit",
    color: "#424242",
    transition: "background-color 0.12s ease",
    "&:hover": {
      backgroundColor: theme.palette.primary.main + "0F",
    },
    "&:focus-visible": {
      outline: "none",
      backgroundColor: theme.palette.primary.main + "1F",
    },
  },
  icon: {
    flex: "0 0 auto",
    width: 22,
    height: 22,
    borderRadius: 4,
    backgroundColor: theme.palette.primary.main + "1A",
    color: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1,
    marginTop: 1,
  },
  itemMain: {
    minWidth: 0,
    flex: 1,
  },
  itemLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: "#212121",
    lineHeight: 1.3,
    display: "block",
  },
  itemDesc: {
    fontSize: 11,
    color: "#757575",
    lineHeight: 1.4,
    marginTop: 0,
    whiteSpace: "normal",
    display: "block",
  },
}));

export default function PresetMenu({
  anchorEl,
  open,
  onClose,
  onSelectPreset,
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const groups = groupPresetsByCategory(REGEN_PRESETS);

  const handlePick = (preset) => {
    onSelectPreset?.(preset);
    onClose?.();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: "top", horizontal: "left" }}
      transformOrigin={{ vertical: "bottom", horizontal: "left" }}
      classes={{ paper: classes.paper }}
    >
      {groups.map((group) => (
        <div key={group.category} className={classes.group}>
          <div className={classes.groupLabel}>{t(group.category)}</div>
          {group.items.map((preset) => (
            <button
              key={preset.id}
              type="button"
              className={classes.item}
              onClick={() => handlePick(preset)}
            >
              <span className={classes.icon} aria-hidden="true">
                {preset.icon}
              </span>
              <span className={classes.itemMain}>
                <span className={classes.itemLabel}>{t(preset.labelKey)}</span>
                <span className={classes.itemDesc}>{t(preset.descKey)}</span>
              </span>
            </button>
          ))}
        </div>
      ))}
    </Popover>
  );
}

PresetMenu.propTypes = {
  anchorEl: PropTypes.any,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelectPreset: PropTypes.func.isRequired,
};
